import { forkJoin, forkJoin as observableForkJoin, Subscription } from "rxjs";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  SimpleChanges
} from "@angular/core";
import {
  TreeChart,
  TreeChartEdge,
  TreeChartNode,
  layoutProgram
} from "./plan-layout";
import {
  CalculationPlanService,
  SourceQuestion,
  SourceWithSourceQuestions,
  QuestionService,
  CalculationPlan,
  TaxReturnService
} from "../../../../swaggerServices";
import { Instruction } from "./model/instruction";
import { Program } from "./model/program";
import { Block, BlockType } from "./model/block";
import { CalcPlan } from "./model/calc-plan";
import {
  CalcInput,
  LiteralType,
  NumberLiteral,
  TextLiteral,
  TaxReturnSourceOptions
} from "./model/calc-input";
import { calculationSource, literalSource } from "./model/transient";
import { Tenant } from "../../../shared/types/tenant.enum";
import { ConfigStateService } from "../../config/config-state.service";
import { environment } from "../../../environments/environment";
import { CdkDrag, CdkDragDrop } from "@angular/cdk/drag-drop";

interface ICalculationPlan extends CalculationPlan {
  questionName?: string;
}

type SourceDropData =
  | {
      source: SourceWithSourceQuestions;
      question: SourceQuestion;
      sourceOptions: TaxReturnSourceOptions;
    }
  | CalcInput;

declare const $: any;

@Component({
    selector: "app-calc-plan-editor",
    templateUrl: "./calc-plan-editor.component.html",
    styleUrls: ["./calc-plan-editor.component.scss"],
    standalone: false
})
export class CalcPlanEditorComponent
  implements OnInit, AfterViewInit, OnDestroy {
  subscriptions: Subscription[] = [];
  @Input() calcPlan: CalcPlan;
  @Input() draggingSource: boolean;
  @ViewChild("successModal") successModalRef: ElementRef;
  @ViewChildren("description") descriptionRef: QueryList<ElementRef>;

  BlockType = BlockType; // export enum to the template

  charts: TreeChart[];
  isSyncingCache: boolean;

  selectedProgram?: Program;
  selectedInstructionId: number | null = null;
  hoveredInstructionId: number | null = null;

  isPlanVisiable = false;
  isPlanButtonVisiable: boolean;
  selectedCalcPlan: CalculationPlan;
  loading: boolean;
  questionsById: {
    [id: string]: { question: string; trail: string; year: number };
  };
  calculationPlansOriginal: CalculationPlan[];
  calculationPlans: ICalculationPlan[];
  planJson: string;
  planSearch: string;
  showQuestionId: boolean;
  isProductionMode: boolean;
  skipTrace: boolean;

  @Output() newPlanLoaded: EventEmitter<string> = new EventEmitter<string>();

  // The flowchart is overlaid on to a virtual grid. Each block sits in the center of a grid cell.
  gridSize: number = 120;
  blockSizes: Map<BlockType, { w: number; h: number }> = new Map<
    BlockType,
    { w: number; h: number }
  >([
    [BlockType.Start, { w: 54, h: 54 }],
    [BlockType.End, { w: 54, h: 54 }],
    [BlockType.Value, { w: 54, h: 54 }],
    [BlockType.Decision, { w: 90, h: 90 }],
    [BlockType.Function, { w: 96, h: 50 }],
    [BlockType.Warning, { w: 70, h: 50 }]
  ]);

  constructor(
    private calcPlanService: CalculationPlanService,
    private config: ConfigStateService,
    private taxReturnService: TaxReturnService,
    private questionService: QuestionService
  ) {
    this.isProductionMode = environment.production;
  }

  ngOnInit() {
    this.charts = this.calcPlan.programs.map(program =>
      this.layoutProgram(program)
    );
    this.setPlanButtonFlag();
    this.showQuestionId = this.config.tenant !== Tenant.AU;
    this.skipTrace = !!this.calcPlan.skipTrace;
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.descriptionRef.changes.subscribe(() => {
        this.descriptionRef.toArray().forEach(elRef => {
          setTimeout(() => {
            this.resetTextAreaHeight(elRef.nativeElement);
          }, 0);
        });
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.calcPlan) {
      this.charts = this.calcPlan.programs.map(program =>
        this.layoutProgram(program)
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  layoutProgram(program: Program) {
    return layoutProgram(program, this.gridSize, this.blockSizes);
  }

  relayoutProgram(program: Program) {
    this.charts = this.charts.map(c =>
      c.program === program ? this.layoutProgram(program) : c
    );
  }

  chartTrackBy(index: number, chart: TreeChart) {
    return index;
  }

  nodeTrackBy(index: number, node: TreeChartNode) {
    return node.instruction.id;
  }

  edgeTrackBy(index: number, edge: TreeChartEdge) {
    return edge.id;
  }

  dropBlockAllowed(chart: TreeChart, edge: TreeChartEdge) {
    return (drag: CdkDrag<Block>) => {
      const blockDragData = drag.data;
      if (blockDragData.type) {
        const programIndex = this.calcPlan.programs.indexOf(chart.program);
        if (programIndex === 0) {
          // can always drop non-value blocks
          // can only drop a value block on an end black or a placeholder block
          return (
            blockDragData.type !== BlockType.Value ||
            edge.nextNode.instruction.block.type === BlockType.End ||
            edge.nextNode.instruction.block.type === BlockType.Placeholder
          );
        } else {
          // secondary programs cannot drop value blocks
          return blockDragData.type !== BlockType.Value;
        }
      }
    };
  }

  dropBlockOnEdge(e: CdkDragDrop<Block, null, Block>, chart: TreeChart, edge: TreeChartEdge) {
    if (!e.isPointerOverContainer) {
      return;
    }
    const block = e.item.data;
    const instructionId = edge.node.instruction.id;
    this.calcPlan.addInstructionAfter(
      chart.program,
      instructionId,
      edge.isRight,
      block
    );
    this.relayoutProgram(chart.program);
    this.isPlanVisiable = false;
    this.setPlanButtonFlag();
  }

  selectNode(node: TreeChartNode, chart?: TreeChart) {
    this.isPlanVisiable = false;
    this.setPlanButtonFlag();

    if (node.instruction.block.type !== BlockType.Placeholder) {
      this.selectedInstructionId = node.instruction.id;
      if (chart) {
        this.selectedProgram = chart.program;
      }
      this.hoveredInstructionId = null;
    }
  }

  unselectNode() {
    this.selectedProgram = null;
    this.selectedInstructionId = null;
  }

  get selectedNode() {
    return this.selectedInstructionId !== null
      ? this.selectedChart.nodesByInstructionId[this.selectedInstructionId]
      : null;
  }

  get selectedChart() {
    return !!this.selectedProgram
      ? this.charts.find(c => c.program === this.selectedProgram)
      : null;
  }

  canDeleteNode(node: TreeChartNode) {
    return node.instruction.block.type !== BlockType.End;
  }

  canCopyNode(node: TreeChartNode) {
    return (
      node.instruction.block.type !== BlockType.End &&
      node.instruction.block.type !== BlockType.Start
    );
  }

  copyNode(node: TreeChartNode) {
    const chart = this.selectedChart;
    this.calcPlan.addInstructionAfter(
      chart.program,
      node.instruction.id,
      true,
      node.instruction.block,
      node.instruction.inputs
    );
    this.relayoutProgram(chart.program);
  }

  copyNodeDown(node: TreeChartNode) {
    const chart = this.selectedChart;
    this.calcPlan.addInstructionAfter(
      chart.program,
      node.instruction.id,
      false,
      node.instruction.block,
      node.instruction.inputs
    );
    this.relayoutProgram(chart.program);
  }

  deleteNode(node: TreeChartNode) {
    const chart = this.selectedChart;
    if (node.instruction.block.type === BlockType.Start) {
      const programIndex = this.calcPlan.programs.indexOf(chart.program);
      if (programIndex === 0) {
        // cannot delete the first program, only truncate it
        this.calcPlan.emptyOutProgram(chart.program);
        this.relayoutProgram(chart.program);
      } else {
        this.calcPlan.deleteProgram(chart.program);
        const index = this.charts.indexOf(chart);
        this.charts.splice(index, 1);
      }
    } else {
      this.calcPlan.deleteInstruction(chart.program, node.instruction.id);
      this.relayoutProgram(chart.program);
    }
    this.selectedInstructionId = null;
    this.selectedProgram = null;
    this.setPlanButtonFlag();
  }

  dropSourceOnNode(
    dropEvent: CdkDragDrop<SourceDropData, string, SourceDropData>,
    node: TreeChartNode,
    index: number
  ) {
    if (!dropEvent.isPointerOverContainer) {
      return;
    }
    const data =  dropEvent.item.data;
    const calcInput =
      data instanceof CalcInput
        ? data
        : new CalcInput(
            data.source,
            data.question,
            data.sourceOptions,
            this.calcPlan.isGlobalCalcPlan
          );
    const currentIndex = node.instruction.inputs.indexOf(calcInput);
    if (currentIndex >= 0) {
      // if moving after, splice it out, then put it back in at index - 1
      if (index > currentIndex) {
        node.instruction.inputs.splice(currentIndex, 1);
        node.instruction.inputs.splice(index - 1, 0, calcInput);
      } else if (index < currentIndex) {
        // if moving before, splice it out, then put it back in at index
        node.instruction.inputs.splice(currentIndex, 1);
        node.instruction.inputs.splice(index, 0, calcInput);
      }
    } else {
      node.instruction.inputs.splice(index, 0, calcInput);
    }
  }

  dropSourceOnValue(
    dropEvent: CdkDragDrop<SourceDropData, string, SourceDropData>,
    node: TreeChartNode
  ) {
    if (!dropEvent.isPointerOverContainer) {
      return;
    }
    const data =  dropEvent.item.data;
    const calcInput =
      data instanceof CalcInput
        ? data
        : new CalcInput(
            data.source,
            data.question,
            data.sourceOptions,
            this.calcPlan.isGlobalCalcPlan
          );
    node.instruction.inputs.length = 0;
    node.instruction.inputs.push(calcInput);
  }

  deleteCalcInput(node: TreeChartNode, calcInput: CalcInput) {
    const index = node.instruction.inputs.indexOf(calcInput);
    node.instruction.inputs.splice(index, 1);
  }

  resolveInput(
    program: Program,
    instruction: Instruction,
    calcInput: CalcInput
  ) {
    if (calcInput.source === calculationSource) {
      return program.resolveIntermediateCalcInput(instruction, calcInput);
    }
    return calcInput;
  }

  instructionHovered(instruction: Instruction | null) {
    this.hoveredInstructionId = instruction ? instruction.id : null;
  }

  canDragNode(program: Program, node: TreeChartNode) {
    if (!this.selectedNode || this.selectedNode === node) {
      return false;
    }
    if (node.instruction.block.type !== BlockType.Function) {
      return false;
    }
    const path = program.pathToInstruction(this.selectedNode.instruction);
    return path != null && path.some(i => i.id === node.instruction.id);
  }

  instructionAsDragData(instruction: Instruction) {
    return new CalcInput(
      calculationSource,
      instruction.id,
      null,
      this.calcPlan.isGlobalCalcPlan
    );
  }

  addNewStartPoint() {
    const program = this.calcPlan.addNewProgram();
    this.charts.push(this.layoutProgram(program));
  }

  addLiteral(node: TreeChartNode) {
    const literal: TextLiteral | NumberLiteral =
      node.instruction.block.type === BlockType.Warning
        ? { type: LiteralType.Text, value: "" }
        : { type: LiteralType.Text, value: null };
    node.instruction.inputs.push(
      new CalcInput(
        literalSource,
        literal,
        null,
        this.calcPlan.isGlobalCalcPlan
      )
    );
  }

  savePlan() {
    const body: any = {
      id: this.calcPlan.id || null,
      plan: this.calcPlan.toPlanJson(),
      skipTrace: this.skipTrace
    };
    this.calcPlanService.updateCalculationPlan(body).subscribe(() => {
      $(this.successModalRef.nativeElement).modal("show");
    });
  }

  listAvailablePlans() {
    this.isPlanVisiable = true;
    this.loading = true;
    const getPlans = this.calcPlanService.getCalculationPlans();
    const getQuestions = this.questionService.getQuestionTrail(
      this.calcPlan.question.year
    );

    observableForkJoin([getPlans, getQuestions]).subscribe(
      ([calculationPlans, questionTrail]) => {
        this.loading = false;
        this.calculationPlansOriginal = calculationPlans.calculationPlans;
        this.handleQuestionTrail(questionTrail);
        this.calculationPlans.forEach(
          cp => (cp.questionName = this.questionsById[cp.questionId].question)
        );
      },
      () => {
        this.loading = false;
      }
    );
  }

  handleQuestionTrail(questionTrail) {
    this.questionsById = {};
    questionTrail.forEach(q => {
      this.questionsById[q.id] = {
        question: q.question,
        trail: q.trail.join(", "),
        year: q.year
      };
    });
    this.calculationPlans = this.calculationPlansOriginal.filter(
      cp =>
        cp.status !== 99 &&
        this.questionsById[cp.questionId] &&
        cp.plan.includes("id")
    );
  }

  selectPlan(calcPlan: CalculationPlan): void {
    //
    this.selectedCalcPlan = calcPlan;
    this.newPlanLoaded.emit(calcPlan.plan);
  }

  questionLabels() {
    const q: any = this.calcPlan.question;
    const dataType = (q.dataType || "").toLowerCase();

    const labels = [];

    if (dataType) {
      labels.push(dataType);
    }
    if (q.isPercentage) {
      labels.push("%");
    }
    if (q.isCurrency) {
      labels.push("$");
    }
    if (q.isTab) {
      labels.push("isTab");
    } else if (q.inGroup) {
      labels.push("inTab");
    }
    if (q.isTableColumn) {
      labels.push("isTableColumn");
    }
    return labels.length ? `(${labels.join(", ")})` : "";
  }

  replaceBlockAllowed(node: TreeChartNode) {
    return (drag: CdkDrag<Block>) => {
      const blockDragData = drag.data;
      return blockDragData && blockDragData.type === node.instruction.block.type;
    };
  }

  replaceBlock(e: CdkDragDrop<Block, string, Block>, node: TreeChartNode) {
    if (!e.isPointerOverContainer) {
      return;
    }
    const block = e.item.data;
    if (block.type === node.instruction.block.type) {
      if (node.instruction.name === node.instruction.block.name) {
        node.instruction.name = block.name;
      }
      node.instruction.block = block;
    }
  }

  swapDecisionDirection(chart: TreeChart, node: TreeChartNode) {
    const { yes, no } = node.instruction;
    node.instruction.yes = no;
    node.instruction.no = yes;
    this.relayoutProgram(chart.program);
  }

  descriptionChanged(event: Event) {
    const textarea: any = event.target;
    this.resetTextAreaHeight(textarea);
  }

  resetTextAreaHeight(el) {
    // hack to auto resize textareas since they can't do it by themselves to fit their content...
    el.setAttribute("style", "height:0;overflow-y:hidden;");
    el.setAttribute("style", `height:${el.scrollHeight}px;overflow-y:hidden;`);
  }

  setPlanButtonFlag() {
    this.planJson = this.calcPlan.toPlanJsonString();
    if (this.planJson.includes("id")) {
      this.isPlanButtonVisiable = false;
    } else {
      this.isPlanButtonVisiable = true;
    }
  }

  syncCache() {
    this.isSyncingCache = true;
    this.subscriptions.push(
      forkJoin([
        this.taxReturnService.syncTaxReturnStructure(),
        this.taxReturnService.syncGlobalStructure()
      ]).subscribe(() => {
        this.isSyncingCache = false;
      })
    );
  }

  sourceDropAllowed = (drag: CdkDrag<unknown>) => {
    return this.isSourceDragData(drag.data);
  }

  private isSourceDragData(data: any): boolean {
    if (!data) {
      return false;
    }

    if (data instanceof CalcInput) {
      return true;
    }

    return Boolean(data.source && data.question);
  }

  falseListEnterPredicate() {
    return false;
  }
}
